import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useCart } from '../../context/CartContext';
import { useLang } from '../../i18n';
import { useProduct, eur, getProductByHandle, SHOPIFY_READY } from '../../lib/shopify';
import { track } from '../../lib/fbpixel';
import { SmartImg } from '../Image';
import { FadeUp } from '../motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

// The bundle is a REAL separate Shopify product (2-of-us / two-of-us) —
// never quantity 2 of the single earphones
function useBundleProduct() {
  const [bundle, setBundle] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      let p = await getProductByHandle('2-of-us');
      if (!p || (SHOPIFY_READY && p.mock)) p = await getProductByHandle('two-of-us');
      if (alive) setBundle(p);
    })().catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return bundle;
}

export default function ProductSection() {
  const { product } = useProduct('01-chrome');
  const bundleProduct = useBundleProduct();
  const { addLine, busy } = useCart();
  const { t } = useLang();
  const variants = product?.variants || [];

  const firstAvailable = useMemo(
    () => variants.find((v) => v.available) || variants[0] || null,
    [variants]
  );
  const [selectedId, setSelectedId] = useState(null);
  const [pack, setPack] = useState('single');
  const selected = variants.find((v) => v.id === selectedId) || firstAvailable;
  const bundleVariant =
    bundleProduct?.variants?.find((v) => v.available) ||
    bundleProduct?.variants?.[0] ||
    null;
  const bundleUnavailable =
    !bundleVariant || (SHOPIFY_READY && Boolean(bundleProduct?.mock));
  const isBundle = pack === 'bundle' && !bundleUnavailable;
  const activeProduct = isBundle ? bundleProduct : product;
  const activeVariant = isBundle ? bundleVariant : selected;
  const activePrice = Number(
    activeVariant?.price ??
      (isBundle ? bundleProduct?.price ?? 54.9 : product?.price ?? 29.9)
  );

  // Meta Pixel — ViewContent, fired once when the 01 / CHROME product is viewed
  const sectionRef = useRef(null);
  const viewFiredRef = useRef(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || viewFiredRef.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !viewFiredRef.current && product) {
            viewFiredRef.current = true;
            track('ViewContent', {
              content_type: 'product',
              content_ids: [product?.handle || '01-chrome'],
              content_name: product?.title || '01 / CHROME',
              value: Number(product?.price || 0),
              currency: 'EUR',
            });
            io.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [product]);

  return (
    <section
      id="shop"
      ref={sectionRef}
      data-testid="product-section"
      className="scroll-mt-32 bg-white px-4 py-20 sm:px-8 sm:py-28 md:scroll-mt-36 lg:px-16"
    >
      <div className="flex flex-col md:grid md:grid-cols-[1.05fr_1fr] md:gap-x-14 lg:gap-x-20">
        {/* block A — label, title, subtitle, price, description */}
        <div className="min-w-0">
          <FadeUp>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              01 — {t('product.label')}
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <div className="mt-6">
              <SmartImg
                src="/assets/title-chrome.png"
                alt="01 / CHROME"
                label="01 / CHROME"
                className="w-44 sm:w-72"
              />
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                {t('product.subtitle')}
              </p>
              <p className="mt-6 font-mono text-[9px] uppercase tracking-[0.28em] text-neutral-500">
                {t('product.dropPrice')}
              </p>
              <p
                data-testid="product-price"
                className="mt-2 flex items-baseline gap-3 font-mono text-lg uppercase tracking-[0.3em]"
              >
                {eur(activePrice)}
                {!isBundle && (
                  <span
                    data-testid="product-compare-price"
                    className="text-sm tracking-[0.2em] text-neutral-400 line-through"
                  >
                    {eur(59.9)}
                  </span>
                )}
              </p>
            </div>
          </FadeUp>

          <FadeUp delay={0.2} className="mt-8 max-w-sm">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em]">
              {t('product.tagline')}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-neutral-600">
              {t('product.desc')}
            </p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              {t('product.outro')}
            </p>
          </FadeUp>
        </div>

        {/* product still — in normal flow on mobile, right column on desktop */}
        <FadeUp
          delay={0.15}
          className="my-12 flex justify-center md:my-0 md:row-span-2 md:self-center"
        >
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            className="w-[74vw] max-w-[320px] md:w-[85%] md:max-w-[600px]"
          >
            <SmartImg
              src="/assets/packaging-open.png"
              alt="01 / CHROME — auricolari cablati in cromo"
              label="01 / Chrome — Immagine prodotto in arrivo"
              className="h-auto w-full object-contain"
            />
          </motion.div>
        </FadeUp>

        {/* block B — connector detail, pack choice, preorder, CTA, accordions */}
        <div className="min-w-0">
          <FadeUp delay={0.24}>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              {t('product.connector')}
            </p>
            <p
              data-testid="connector-single"
              className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em]"
            >
              USB-C
            </p>
          </FadeUp>

          <FadeUp delay={0.3}>
            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              {t('pack.label')}
            </p>
            <div className="mt-4 grid max-w-md gap-2">
              {variants.map((variant, i) => {
                const title =
                  variant.title === 'Default Title' ? 'JUST 01' : variant.title;
                const active = !isBundle && selected?.id === variant.id;
                return (
                  <button
                    key={variant.id}
                    type="button"
                    data-testid={`pack-option-${i}`}
                    disabled={!variant.available}
                    onClick={() => {
                      setSelectedId(variant.id);
                      setPack('single');
                    }}
                    className={`flex items-center justify-between gap-4 border px-4 py-3.5 text-left transition-colors duration-300 ${
                      active
                        ? 'border-black bg-black text-bone'
                        : 'border-black/15 hover:border-black'
                    } ${!variant.available ? 'cursor-not-allowed opacity-35' : ''}`}
                  >
                    <span>
                      <span className="block font-mono text-[11px] uppercase tracking-[0.25em]">
                        {title}
                      </span>
                      <span
                        className={`mt-1 block font-mono text-[9px] uppercase tracking-[0.2em] ${
                          active ? 'text-bone/60' : 'text-neutral-500'
                        }`}
                      >
                        1 × 01 / Chrome
                      </span>
                    </span>
                    <span className="font-mono text-[11px] tracking-[0.15em]">
                      {eur(variant.price)}
                    </span>
                  </button>
                );
              })}
              {bundleProduct && (
                <button
                  type="button"
                  data-testid="pack-option-bundle"
                  disabled={bundleUnavailable}
                  onClick={() => setPack('bundle')}
                  className={`flex items-center justify-between gap-4 border px-4 py-3.5 text-left transition-colors duration-300 ${
                    isBundle
                      ? 'border-black bg-black text-bone'
                      : 'border-black/15 hover:border-black'
                  } ${bundleUnavailable ? 'cursor-not-allowed opacity-35' : ''}`}
                >
                  <span>
                    <span className="block font-mono text-[11px] uppercase tracking-[0.25em]">
                      {t('pack.two')}
                    </span>
                    <span
                      className={`mt-1 block font-mono text-[9px] uppercase tracking-[0.2em] ${
                        isBundle ? 'text-bone/60' : 'text-neutral-500'
                      }`}
                    >
                      {t('pack.twoSub')} — {t('pack.free')}
                    </span>
                  </span>
                  <span className="font-mono text-[11px] tracking-[0.15em]">
                    {eur(Number(bundleVariant?.price ?? bundleProduct.price ?? 54.9))}
                  </span>
                </button>
              )}
            </div>
            <p
              data-testid="pack-shipping-line"
              className="mt-3 max-w-md font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500"
            >
              {isBundle
                ? `${eur(activePrice)} — ${t('pack.free')}`
                : `${eur(activePrice)} + €4,90 ${t('pack.shipWord')}`}
            </p>
            <p
              data-testid="pack-free-note"
              className="mt-1.5 max-w-md font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400"
            >
              {t('pack.freeOver')}
            </p>
          </FadeUp>

          <FadeUp delay={0.36}>
            <div
              data-testid="preorder-box"
              className="mt-8 max-w-md border border-black/15 p-4"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.3em]">
                {t('preorder.title')}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                {t('preorder.body')}
              </p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                {t('preorder.shipping')}
              </p>
            </div>
            <button
              type="button"
              data-testid="product-add-to-bag"
              disabled={!activeVariant?.available || busy}
              onClick={() => addLine(activeProduct, activeVariant, 1)}
              className="mt-6 w-full rounded-full bg-ink py-4 font-mono text-[11px] uppercase tracking-[0.35em] text-bone transition-colors duration-300 hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:px-12"
            >
              {t('product.add')}
            </button>
          </FadeUp>

          <FadeUp delay={0.44}>
            <Accordion type="single" collapsible className="mt-10 max-w-md">
              <AccordionItem value="details" className="border-black/10">
                <AccordionTrigger
                  data-testid="accordion-details"
                  className="font-mono text-[10px] uppercase tracking-[0.3em] hover:no-underline"
                >
                  {t('product.details')}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-neutral-600">
                  {t('product.detailsBody')}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="compat" className="border-black/10">
                <AccordionTrigger
                  data-testid="accordion-compatibility"
                  className="font-mono text-[10px] uppercase tracking-[0.3em] hover:no-underline"
                >
                  {t('product.compat')}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-neutral-600">
                  {t('product.compatBody')}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="shipping" className="border-black/10">
                <AccordionTrigger
                  data-testid="accordion-shipping"
                  className="font-mono text-[10px] uppercase tracking-[0.3em] hover:no-underline"
                >
                  {t('product.shipping')}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-neutral-600">
                  {t('product.shippingBody')}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
